import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Hammer, LogOut, ChevronRight, Search, Plus, MapPin } from "lucide-react";
import ArtisanTrustPass from "@/components/ArtisanTrustPass";
import { ArtisanMapPanel } from "@/components/map/ArtisanMapPanel";
import { SubscriptionWalletCard } from "@/components/subscription/SubscriptionWalletCard";
import RoleSwitcher from "@/components/RoleSwitcher";
import { AccountMenu } from "@/components/AccountMenu";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_LABELS = {
  open: "Ouvert · en attente de propositions",
  proposed: "Proposition envoyée",
  in_progress: "En cours",
  completed: "Complété",
  released: "Fonds libérés",
  disputed: "Litige en cours",
  resolved_disputed: "Litige résolu",
};

const STATUS_COLOR = {
  open: "text-slate-300 bg-slate-500/10",
  proposed: "text-blue-300 bg-blue-500/10",
  in_progress: "text-[#D4AF37] bg-[#D4AF37]/10",
  completed: "text-emerald-300 bg-emerald-500/10",
  released: "text-emerald-300 bg-emerald-500/10",
  disputed: "text-red-300 bg-red-500/10",
  resolved_disputed: "text-orange-300 bg-orange-500/10",
};

export default function ArtisanDashboard({ user, onLogout, onUserUpdate }) {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchProjects = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      // Projets où l'artisan est sélectionné ou a soumis une proposition
      const r = await axios.get(`${API}/artisans/projects/by-artisan/${user.id}`).catch(() => null);
      if (r?.data) {
        setProjects(r.data);
      } else {
        // Fallback : list public projects et filtrer côté front
        const all = await axios.get(`${API}/artisans/projects?limit=100`).catch(() => ({ data: [] }));
        const mine = (all.data || []).filter(
          (p) => p.selected_artisan_id === user.id || (p.proposals || []).some((pr) => pr.artisan_id === user.id)
        );
        setProjects(mine);
      }
    } catch (err) {
      console.warn("Projects fetch failed:", err);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  return (
    <div className="min-h-screen bg-[#0F172A] text-white pb-16" data-testid="artisan-dashboard">
      {/* HEADER */}
      <header className="bg-[#1E293B]/80 backdrop-blur-xl border-b border-white/5 sticky top-0 z-40 px-5 md:px-8 py-4 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <img
            src="/woma-logo.png"
            alt="WÔMA de ARTEKNOS"
            className="w-10 h-10 rounded-full object-cover ring-2 ring-[#D4AF37]/50 shadow-[0_0_12px_rgba(212,175,55,0.3)] flex-shrink-0"
            data-testid="artisan-header-logo"
          />
          <div>
            <h1 className="text-sm md:text-base font-black tracking-tight italic uppercase">
              <span className="text-[#D4AF37]">WÔMA</span> <span className="text-white">Artisan</span>
            </h1>
            <p className="text-[9px] text-slate-400 uppercase tracking-[0.3em] font-bold">Espace pro · {user?.full_name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <RoleSwitcher user={user} onUserUpdate={onUserUpdate} className="hidden sm:block" />
          <button
            onClick={onLogout}
            className="hidden sm:block p-2.5 rounded-xl bg-white/5 border border-white/10 text-slate-400 hover:text-red-400 hover:border-red-400/40"
            data-testid="artisan-logout-btn"
          >
            <LogOut className="w-4 h-4" />
          </button>
          <AccountMenu user={user} onUserUpdate={onUserUpdate} onLogout={onLogout} className="sm:hidden" />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 md:px-8 py-6 space-y-6">
        {/* ============ TRUST PASS (top priority) ============ */}
        <ArtisanTrustPass user={user} />

        <SubscriptionWalletCard user={user} />

        <ArtisanMapPanel user={user} />

        {/* ============ QUICK ACTIONS ============ */}
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => navigate("/artisans")}
            className="bg-[#1E293B] hover:bg-[#1E293B]/70 border border-white/5 hover:border-[#D4AF37]/30 rounded-2xl p-4 text-left transition-all active:scale-[0.98]"
            data-testid="artisan-action-browse"
          >
            <Search className="w-5 h-5 text-[#D4AF37] mb-2" />
            <p className="text-xs font-black uppercase tracking-tight">Découvrir</p>
            <p className="text-[10px] text-slate-500 mt-0.5">Profils & projets</p>
          </button>
          <button
            onClick={() => navigate("/artisans/profile/edit")}
            className="bg-[#1E293B] hover:bg-[#1E293B]/70 border border-white/5 hover:border-[#D4AF37]/30 rounded-2xl p-4 text-left transition-all active:scale-[0.98]"
            data-testid="artisan-action-edit-profile"
          >
            <MapPin className="w-5 h-5 text-[#D4AF37] mb-2" />
            <p className="text-xs font-black uppercase tracking-tight">Mon profil</p>
            <p className="text-[10px] text-slate-500 mt-0.5">Photos & tarifs</p>
          </button>
        </div>

        {/* ============ MES PROJETS ============ */}
        <section>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xs font-black uppercase tracking-[0.25em] text-slate-400">Mes projets</h3>
            <button
              onClick={() => navigate("/artisans")}
              className="text-[10px] font-black uppercase tracking-widest text-[#D4AF37] hover:opacity-80"
            >
              Voir tous
            </button>
          </div>

          {loading ? (
            <div className="bg-[#1E293B] rounded-2xl py-8 text-center text-slate-500 text-xs">Chargement…</div>
          ) : projects.length === 0 ? (
            <div className="bg-[#1E293B] border border-dashed border-white/10 rounded-2xl py-8 px-5 text-center" data-testid="no-projects-state">
              <Plus className="w-8 h-8 text-slate-700 mx-auto mb-2" />
              <p className="text-sm text-slate-300 font-bold">Aucun projet pour l'instant</p>
              <p className="text-[10px] text-slate-500 uppercase tracking-widest mt-1">
                Parcourez les demandes ouvertes pour soumettre une proposition
              </p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="artisan-projects-list">
              {projects.map((p) => (
                <button
                  key={p.id}
                  onClick={() => navigate(`/artisans/projects/${p.id}`)}
                  className="w-full bg-[#1E293B] hover:bg-[#1E293B]/70 border border-white/5 hover:border-[#D4AF37]/30 rounded-2xl p-4 text-left transition-all flex items-center justify-between gap-3"
                  data-testid={`artisan-project-${p.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-black uppercase tracking-tight truncate text-white">{p.title}</p>
                    <p className="text-[10px] text-slate-500 mt-0.5 uppercase tracking-widest">
                      {p.category} · {Number(p.budget || 0).toLocaleString("fr-FR")} FCFA
                    </p>
                    <span
                      className={`inline-block mt-2 px-2 py-0.5 rounded-full text-[9px] font-black uppercase tracking-widest ${STATUS_COLOR[p.status] || "text-slate-400 bg-white/5"}`}
                    >
                      {STATUS_LABELS[p.status] || p.status}
                    </span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-500 flex-shrink-0" />
                </button>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
